export enum AnalyticsEvent {
  VIDEO_CLICK = "video_clicked",
  VIDEO_TRACK = "video_tracked",
  VIDEO_UNMUTE = "video_unmuted",
  VIDEO_ERROR = "video_errored",
  SIMULATION_LOADED = "simulation_loaded",
  SIMULATION_STARTED = "simulation_started",
  SIMULATION_EXITED = "simulation_exited",
  SIMULATION_ERRORED = "simulation_errored",
  TECHNIQUE_EXPANDED = "technique_expansion_clicked",
  FAQ_EXPANDED = "faqs_expanded",
  CTA_HOMEPAGE = "homepage_cta_clicked",
  CTA_PRICING = "pricing_cta_clicked",
  LINK_CLICK = "link_clicked",
  BUTTON_CLICK = "button_clicked",
  CANCEL_TRRIAL = "cancel_trial",
  ENTER_EXPERIMENT = "experiment_entered",
  VIEW_ITEM_LIST = "view_item_list",
  SELECT_ITEM = "select_item",
  ADD_TO_CART = "add_to_cart",
  SCROLL_DEPTH = "scroll_depth",
  AUTH_ERROR = "auth_error",
  PASSWORD_RESET_ON_RETURN = "password_reset_on_return",
  PASSWORD_MIGRATED_JIT = "password_migrated_jit",
  COPY_TEXT = "text_copied",
  FIRST_NMH_ACCESS = "first_nmh_access",
  BEGIN_CHECKOUT = "begin_checkout",
  SIGN_UP = "sign_up",
  CHECKOUT_FORM_BUILT = "checkout_form_built",
  CHECKOUT_PAYMENT_METHOD_SELECTED = "checkout_payment_method_selected",
  CHECKOUT_PAYMENT_METHOD_ENTERED = "checkout_payment_method_entered",
  CHECKOUT_SUBMITTED = "checkout_submitted",
  CHECKOUT_SUBMIT_FAILED = "checkout_submit_failed",
  CHECKOUT_ERRORED_DROPIN_CREATE = "checkout_errored_dropin_create",
  CHECKOUT_ERRORED_PAYMENT_FORM = "checkout_errored_payment_form",
  CHECKOUT_API_UNREACHABLE = "checkout_api_unreachable",
  CLIENT_SIDE_PURCHASE = "client_side_purchase",
  CONVERT_PURCHASE_USD = "convert_purchase_usd",
  ACCOUNT_FORM_COMPLETED = "account_form_completed",
  HUMAN_VERIFICATION_SUCCESS = "human_verification_success",
  HUMAN_VERIFICATION_FAILURE = "human_verification_failure",
  HUMAN_VERIFICATION_EXPIRED = "human_verification_expired",
  HUMAN_VERIFICATION_UNSUPPORTED = "human_verification_unsupported",
  HUMAN_VERIFICATION_HUNG = "human_verification_hung",
  // OMG-2313 card-form micro-funnel: checkout_payment_method_selected ->
  // _entered spans the ENTIRE card form, so these locate where inside it a
  // buyer stalls. Fired on both arms (the Drop-in card form is shared); the
  // email pair is payment-first-only, where the card step owns the email field.
  CHECKOUT_CARD_FIELD_ENGAGED = "checkout_card_field_engaged",
  CHECKOUT_CARD_FIELD_VALID = "checkout_card_field_valid",
  CHECKOUT_CARD_EMAIL_ENGAGED = "checkout_card_email_engaged",
  CHECKOUT_CARD_EMAIL_VALID = "checkout_card_email_valid",
  CHECKOUT_CARD_SUBMIT_REJECTED = "checkout_card_submit_rejected",
  // OMG-2516: iOS focus-bounce correction fired (tap focused a field, WebKit's
  // post-focus scroll made the synthesized click land on a neighbour, we
  // snapped focus back). Counts how often the quirk actually bites.
  CHECKOUT_CARD_FOCUS_BOUNCE_CORRECTED = "checkout_card_focus_bounce_corrected",
  // OMG-2338: fired when the card form released focus after an iOS autofill
  // so the keyboard + autofill bar stop covering Buy Now. Counts how often the
  // dismissal actually ran, not how often autofill happened.
  CHECKOUT_CARD_AUTOFILL_DISMISSED = "checkout_card_autofill_dismissed",
  // OMG-2753: lean-back mode on technique pages (OMG-2747). Positions and
  // counts only, never which technique or video (scrubEvent.ts).
  LEANBACK_STARTED = "leanback_started",
  LEANBACK_ADVANCED = "leanback_advanced",
  LEANBACK_COMPLETED = "leanback_completed",
  LEANBACK_EXITED = "leanback_exited",
}

export enum Storage {
  APP = "app_state",
}

export enum Cookie {
  COUNTRY = "NEXT_COUNTRY",
  HOLIDAY = "holiday_modal",
  VISITED = "visited",
  EXPERIMENT = "_gaexp_omgyes",
  VARIANT = "_gaexp_variant",
  DISCOUNT = "discountCode",
  UTMSOURCE = "utm_src",
}

export enum NavMode {
  PRICING = "pricing",
  DEFAULT = "default",
  SIMPLE = "simple",
  SIMPLE_CATHY = "simple_cathy",
  NONE = "none",
}

export enum FooterMode {
  DEFAULT = "default",
  SIMPLE = "simple",
  SIMPLE_CATHY = "simple_cathy",
}

export enum Environment {
  PROD = "prod",
  PROD_GO = "prod-go",
  UAT = "uat",
  DEV = "dev",
  LOCAL = "local",
  ALL = "all",
}

export enum Theme {
  V1 = "omgyes-v1",
  V2 = "omgyes-v2",
  NMH = "omgyes-nmh",
}

export enum MediaType {
  VIDEO = "video",
  TEXT = "text",
  IMAGE = "image",
}

export enum TestimonialCategory {
  COUPLES = "Couples",
  WOMEN = "Women",
  MEN = "Men",
}

export type TechniqueSeason = "season-1" | "season-2" | "season-3";
export type SeriesName = "series1" | "series2" | "series3";
export type CollectionName = "essentials" | "inner-pleasure" | "toy-pleasure";

export enum TechniqueCategory {
  PHYSICAL_TOUCH = "Physical Touch",
  MENTAL = "Mental",
  EXPLORATORY = "Exploratory",
}

export enum TextTag {
  P = "p",
  SPAN = "span",
}

export enum TextHeader {
  H1 = "h1",
  H2 = "h2",
  H3 = "h3",
  H4 = "h4",
  H5 = "h5",
  DIV = "div",
}

export enum Route {
  HOME_V1 = "/",
  HOME = "/join",
  PRICING = "/join/pricing#top",
}

export enum SectionID {
  LEARN_FROM = "LearnFrom",
  PRICING = "pricing-info",
  WHAT_IS_OMGYES = "WhatIsOMGYES",
  THE_TECHNIQUES = "TheTechniques",
  OUR_RESEARCH = "OurResearch",
  OUR_MISSION = "OurMission",
  PRESS_AND_REVIEWS = "PressAndReviews",
}

export enum LanguageCode {
  EN = "en",
  DE = "de",
  FR = "fr",
  ES = "es",
  ES_419 = "es_419",
  IT = "it",
  NL = "nl",
  PT = "pt",
  RU = "ru",
  JA = "ja",
  KO = "ko",
  ZH_CN = "zh_CN",
  ZH_HK = "zh_HK",
}

export const LanguageLabels: { [key in LanguageCode]: string } = {
  [LanguageCode.EN]: "English",
  [LanguageCode.DE]: "Deutsch",
  [LanguageCode.FR]: "Français",
  [LanguageCode.ES]: "Español",
  [LanguageCode.ES_419]: "Español (LA)",
  [LanguageCode.IT]: "Italiano",
  [LanguageCode.NL]: "Nederlands",
  [LanguageCode.PT]: "Português",
  [LanguageCode.RU]: "Русский",
  [LanguageCode.JA]: "日本語",
  [LanguageCode.KO]: "한국어",
  [LanguageCode.ZH_CN]: "简体中文",
  [LanguageCode.ZH_HK]: "繁體中文",
};

export const UtilityLinks = [
  { url: "/about#/movement", label: "footer.about" },
  { url: "/faq", label: "footer.faq" },
  { url: "/doctors", label: "footer.clinicians" },
  { url: "/affiliates", label: "footer.affiliates" },
  { url: "/press", label: "footer.press" },
];
export const LegalLinks = [
  { url: "/legal#privacy", label: "footer.privacy" },
  { url: "/legal#terms", label: "footer.terms" },
];

export const PageID: Record<string, string> = {};

const constants = {
  Theme,
  MediaType,
};

export default constants;
